import React, {
    useEffect, useState
} from 'react';
import { FormController } from '../Shared/FormController';
import { AccountsVService, BillingAccountDetails, FindExistingBillingAccountQuery } from '../../services/V2/gozer';
import { useSecurityStatus } from '../../hooks/security';
import { getOperatorId, handleError, isProd } from '../Shared/Functions';
import { Button, CircularProgress, Dialog, DialogContent, DialogTitle } from '@mui/material';
import { withToast } from '../Shared/Toast';
import ReCAPTCHA from "react-google-recaptcha";
import { CaptchaService, $MeCreate, MeCreate, UserUtilityVService } from '../../services/V2/keymaster';
import { FindExistingAccount } from '../Shared/FindExistingAccount';

const MigrateUser = ({ hide, show, fail, username }) => {
    const auth: any = useSecurityStatus();
    const recaptchaRef: any = React.useRef();

    const [user, setUser] = useState<BillingAccountDetails>();
    const [findUser, setFindUser] = useState<FindExistingBillingAccountQuery>({
        accountNumber: "",
        fullName: "",
        operatorId: "",
        hasUser: true,
        districtId: ''
    });
    const [step, setStep] = useState<number>(0);
    const [operatorId, setOperatorId] = useState<string>("");
    const [ready, setReady] = useState<boolean>(true);
    const [createMe, setCreateMe] = useState<MeCreate>({
        billingAccountId: "",
        username: "",
        name: "",
        phoneNumber: "",
        password: "",
        confirmPassword: "",
        operatorId: "",
    });
    const [setToken, setSetToken] = useState<any>();

    useEffect(() => {
        if(step === 0) {
            getOperatorId()
                .then((x:any) => {
                    setCreateMe({
                        ...createMe,
                        operatorId: x ?? ""
                    })
                    setOperatorId(x ?? "")
                })
        } else if(step === 1 && findUser) {
            findAccount()
        }
        if(step === 2) setReady(true);
        if(step === 3) {
            UserUtilityVService.userUtilityVMigrateUser(createMe)
                .then(x => {
                    login()
                })
                .catch(err => {
                    setStep(2);
                    err.message !=="Not Found" && fail(handleError(err))
                })
        }
    }, [step])// eslint-disable-line react-hooks/exhaustive-deps
    
    const login = () => {
        auth.login({
            username: createMe.username,
            password: createMe.password
        })
            .then(x => {})
            .catch(err => err.message !=="Not Found" && fail(handleError(err)))
    }

    const findAccount = async () => {
        var token = setToken;
        if(!setToken){
            await recaptchaRef?.current?.reset();
            token = await recaptchaRef?.current?.executeAsync();
            setSetToken(token)
        }
        CaptchaService.captchaValidate(token)
            .then(x => {
                if(x.success) {
                    AccountsVService.accountsVFindExistingBillingAccount({
                            ...findUser,
                            operatorId: operatorId,
                            hasUser: true
                        })
                        .then(x => {
                            if(x === null) {
                                fail("No active billing account matched your search.")
                                setStep(0);
                                setSetToken(null);
                                setReady(true);
                                return;
                            }
                            setUser(x)
                            setReady(true)
                            setCreateMe({
                                billingAccountId: x?.id ?? "",
                                username: username,
                                name: username,
                                phoneNumber: "",
                                password: "",
                                confirmPassword: "",
                                operatorId: operatorId,
                            })
                        })
                        .catch(err => {
                            setStep(0);
                            err.message !=="Not Found" && fail(handleError(err))
                            setSetToken(null)
                            setReady(true)
                        })
                } else {
                    fail("An error occurred validating the reCaptcha. Please refresh the page to try again. If errors persist, please contact support.")
                    setStep(0);
                    setSetToken(null)
                    setReady(true);
                }
            })
    }

    const handleDlgClose = (event, reason) => {
        if (reason && reason === "backdropClick") {
          return;
        }
    
        hide()
    }

    return (
        <Dialog onClose={handleDlgClose} open={show}>
            <DialogTitle>Link Account</DialogTitle>
            <DialogContent style={{ width: "350px" }}>
                <form style={{ width: "100%" }}>
                    <ReCAPTCHA
                        ref={recaptchaRef}
                        size="invisible"
                        sitekey={isProd() ? "6LdysiEhAAAAAPZwsLD0CCf4U8yJvrD6pkuGuTHw" : "6LeIxAcTAAAAAJcZVRqyHh71UMIEGNQ_MXjiZKhI"}
                    />
                    {step === 0 &&
                        <FindExistingAccount
                            findUser={findUser}
                            setFindUser={setFindUser}
                            operatorId={operatorId}
                        />
                    }
                    {step === 1 ? (ready ?
                        <div style={{ textAlign: "center" }}>
                            <p className="text-muted"><b>Confirm your account.</b></p>
                            <br />
                            <div><b>Account Number</b></div>
                            <div>{user?.accountNumber}</div>
                            <br />
                            <div><b>Name</b></div>
                            <div>{user?.customer?.fullName}</div>
                            <br />
                            <div><b>Address</b></div>
                            <div>{user?.customer?.street}</div>
                            <div>{user?.customer?.city}, {user?.customer?.state} {user?.customer?.zip}</div>
                        </div>
                        :
                        <div style={{ textAlign: "center" }}>
                            <div>Searching for account...</div>
                        </div>) : null
                    }
                    {step === 2 && ready &&
                        <>
                            <p className="text-muted" style={{ textAlign: "center" }}><b>Create a new password.</b></p>
                            <div>Password must contain:</div>
                            <ul style={{ textAlign: "left", fontSize: ".9em" }}>
                                <li>at least 8 characters</li>
                                <li>1 upper case letter</li>
                                <li>1 number</li>
                                <li>at least 1 special character from the following list:<br />{"# ? ! @ $ % ^ & + = < > ( ) _ { } ` , | [ ] - \\"}</li>
                            </ul>
                            <FormController keys={$MeCreate.properties}
                                model={createMe}
                                setModel={setCreateMe}
                                hiddenKeys={["billingaccountid", "operatorid", "username", "name", "phonenumber"]}
                                operatorId={null}
                            />
                        </>
                    }
                    <div className='migrate-user-buttons-group'>
                     <Button variant="outlined"
                            style={{marginTop:"10px", marginRight: "10px", width: "100%"}}
                            id="cancel"
                            onClick={() => hide()}
                            color="primary"
                            type="button">
                                Cancel
                        </Button>
                        <Button variant="contained"
                            style={{marginTop:"10px", width: "100%"}}
                            disabled={!ready}
                            id="create"
                            onClick={() => {
                                setReady(false);
                                setStep(step+1);
                            }}
                            color="primary"
                            type="button">
                            {step === 2 ? "Sign In" : (step === 3 && !ready) ? "Signing In" : "Continue"}
                        </Button>
                    </div>
                    {!ready && <div className='loading-container'><CircularProgress className='center-spinner' color="primary" aria-label="Loading" /></div>}
                </form>
            </DialogContent>
        </Dialog>)
}

export default withToast(MigrateUser);
